import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Download, FolderKanban, Heart, CreditCard, RotateCcw, LifeBuoy, UserCog, Store, Boxes, Package, Inbox, FileText,
  Users, Ticket, Wallet, Star, Shield, UsersRound, KeyRound } from "lucide-react";
import { useMe } from "@/lib/me";
import { RequireAuth, Spinner } from "@/components/shared/common";

const BUY = [["/dashboard", "Overview", LayoutDashboard, true], ["/dashboard/orders", "Orders & contracts", ShoppingBag], ["/dashboard/purchases", "Purchases", Download],
  ["/dashboard/projects", "My projects", FolderKanban], ["/dashboard/favorites", "Favorites", Heart], ["/dashboard/payments", "Payments & invoices", CreditCard],
  ["/dashboard/refunds", "Refunds & disputes", RotateCcw], ["/dashboard/support", "Support", LifeBuoy], ["/dashboard/settings", "Profile settings", UserCog]];
const SELL = [["/dashboard/vendor", "Seller overview", LayoutDashboard, true], ["/dashboard/vendor/storefront", "Storefront", Store], ["/dashboard/vendor/services", "Services", Boxes],
  ["/dashboard/vendor/products", "Products", Package], ["/dashboard/vendor/orders", "Orders", Inbox], ["/dashboard/vendor/proposals", "Proposals", FileText],
  ["/dashboard/vendor/customers", "Customers", Users], ["/dashboard/vendor/coupons", "Coupons", Ticket], ["/dashboard/vendor/payouts", "Earnings & payouts", Wallet],
  ["/dashboard/vendor/reviews", "Reviews", Star], ["/dashboard/vendor/team", "Agency team", UsersRound]];

const Item = ([to, label, Icon, end]) => (
  <NavLink key={to} to={to} end={end} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={({ isActive }) => `flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
    <Icon className="h-4 w-4 shrink-0" />{label}
  </NavLink>
);

export default function DashboardLayout() {
  const { me } = useMe();
  return (
    <RequireAuth>
      <div className="container-x grid gap-8 py-8 lg:grid-cols-12">
        <aside className="lg:col-span-3 xl:col-span-2">
          <div className="flex gap-1 overflow-x-auto pb-2 lg:sticky lg:top-24 lg:flex-col lg:overflow-visible">
            <p className="hidden px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:block">Buying</p>
            {BUY.map(Item)}
            <p className="hidden px-3 pb-1 pt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:block">Selling</p>
            {me?.vendor || me?.team_of?.length ? SELL.filter((s) => s[1] !== "Agency team" || me?.vendor?.type === "agency").map(Item) : Item(["/dashboard/vendor/storefront", "Become a seller", Store])}
            {me?.role === "admin" && <><p className="hidden px-3 pb-1 pt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:block">Platform</p>{Item(["/admin", "Admin console", Shield])}</>}
          </div>
        </aside>
        <main className="min-w-0 lg:col-span-9 xl:col-span-10">{me ? <Outlet /> : <Spinner />}</main>
      </div>
    </RequireAuth>
  );
}

export const DTitle = ({ title, text, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div><h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>{text && <p className="mt-1 text-sm text-muted-foreground">{text}</p>}</div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Metric = ({ label, value, testId, hint }) => (
  <div className="card-surface p-5" data-testid={testId}>
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-2 font-mono text-2xl font-bold">{value}</p>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export function DataTable({ cols, rows, empty = "Nothing here yet.", testId = "data-table" }) {
  if (!rows) return <Spinner />;
  if (!rows.length) return <div className="rounded-xl border border-dashed border-border p-10 text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{empty}</div>;
  return (
    <div className="card-surface overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">{cols.map((c) => <th key={c.h} className="whitespace-nowrap px-4 py-3 font-semibold">{c.h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={r.id || r.user_id || i} className="border-b border-border last:border-0 hover:bg-accent/40">{cols.map((c) => <td key={c.h} className="px-4 py-3 align-top">{c.r(r)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}
